// The drawer every block, marker, row and "+N more" on Today opens: a meeting, a reminder, a due item, or a whole
// day (each item in it opens its own drawer, with Back to the day).
import { Link } from 'react-router-dom';
import type { Reminder, TodayItem } from '@dpw/shared';
import { Sheet } from '../../ui/Sheet';
import { Progress } from '../../ui/bits';
import { Orb } from '../../ui/motion';
import { IconChevL } from '../../ui/icons';
import { dayLabel, localTime } from '../../lib/format';
import { TYPE_CLS, TYPE_COLOR, TYPE_WORD, isMeeting, longDay, progressColor, progressText, remDay } from './util';
import { useAssistantPanel } from '../../assistant/Panel';
import { useActivityDrawer } from '../../shell/Dock';

export type DrawerState =
  | { kind: 'item'; id: string; back?: DrawerBack }
  | { kind: 'reminder'; id: string }
  | { kind: 'day'; date: string }
  | { kind: 'due' };
type DrawerBack = { kind: 'day'; date: string } | { kind: 'due' };


export function TodayDrawer({ state, items, reminders, today, phone, onClose, onOpen, onDone }: {
  state: DrawerState; items: TodayItem[]; reminders: Reminder[]; today: string; phone: boolean;
  onClose: () => void; onOpen: (s: DrawerState) => void; onDone: (r: Reminder) => void;
}) {
  const panel = useAssistantPanel();
  const acts = useActivityDrawer();
  const side = phone ? 'bottom' : 'right';
  if (state.kind === 'day' || state.kind === 'due') {
    const list = state.kind === 'due' ? items.filter(x => x.date === today && !isMeeting(x.type) && !x.start) : items.filter(x => x.date === state.date);
    const title = state.kind === 'due' ? 'Due today, no set time' : longDay(state.date);
    return (
      <Sheet side={side} width={520} label={title} onClose={onClose} crumbs={<><span>{state.kind === 'due' ? 'Today' : 'This week'}</span><span>/</span><b>{state.kind === 'due' ? 'Due today' : dayLabel(state.date)}</b></>}>
        <h2 className="stitle" style={{ fontSize: 22 }}>{title}</h2>
        <span className="xs mut">{state.kind === 'due' ? `${list.length} ${list.length === 1 ? 'item' : 'items'} with a due date of today and no time` : `${list.length} ${list.length === 1 ? 'item' : 'items'}`}</span>
        <div className="col gap8">
          {list.map(x => (
            <button key={x.id} type="button" className="tdcard" style={{ ['--c' as string]: TYPE_COLOR[x.type] }} onClick={() => onOpen({ kind: 'item', id: x.id, back: state })}>
              <span className="dhd"><span className={`ty ${TYPE_CLS[x.type]}`}><i />{TYPE_WORD[x.type]}</span><span className="xs mono mut">{x.start ?? 'Due'}</span></span>
              <span className="t">{x.isMilestone ? '◆ ' : ''}{x.title}</span>
              <span className="m">{x.trackName} · {x.owner?.split(' ')[0] ?? 'No owner'}{!isMeeting(x.type) ? ` · ${progressText(x)}` : ''}</span>
            </button>
          ))}
        </div>
      </Sheet>
    );
  }
  if (state.kind === 'reminder') {
    const r = reminders.find(x => x.id === state.id);
    if (!r) return null;
    const ai = r.setBy === 'assistant';
    const when = r.remindAt ? `${remDay(r) === today ? 'Today' : dayLabel(remDay(r))}, ${localTime(r.remindAt)}` : `${remDay(r) === today ? 'Today' : dayLabel(remDay(r))}, any time`;
    return (
      <Sheet side={side} width={520} label={r.text} onClose={onClose} crumbs={<><span>{r.activity?.spaceName ?? 'Reminders'}</span><span>/</span><b>{r.activity?.title ?? 'Reminder'}</b></>}>
        <div className="dhd"><span className="ty rem"><i />Reminder</span>
          {r.state === 'overdue' && <span className="odb">Overdue</span>}{r.state === 'late' && <span className="odb lt">Late</span>}{r.state === 'done' && <span className="lbl appr">Done</span>}</div>
        <h2 className="stitle" style={{ fontSize: 24 }}>{r.text}</h2>
        <div className="facts">
          <div className="fact"><span className="k">When</span><div className="v">{when}</div></div>
          <div className="fact"><span className="k">Set by</span><div className="v">{ai ? 'The assistant' : r.setByOther ? r.setByUser?.name : 'You'}</div></div>
          <div className="fact"><span className="k">For</span><div className="v">{r.activity?.title ?? 'Nothing linked'}</div></div>
        </div>
        {ai && <div className="dnote ai"><Orb size={10} /><span>Set by the assistant because {r.activity ? `${r.activity.title} still has work left` : 'it looked behind'}. It clears itself when that is done.</span></div>}
        <div className="rowc">
          {r.state !== 'done' && <button type="button" className="bt pri" onClick={() => onDone(r)}>Mark done</button>}
          {r.activity && <button type="button" className="bt" onClick={() => { onClose(); acts.open(r.activity!.id); }}>Open {r.activity.title}</button>}
        </div>
      </Sheet>
    );
  }
  const x = items.find(i => i.id === state.id);
  if (!x) return null;
  const meeting = isMeeting(x.type);
  const flag = x.state.bad ? x.state.label : x.isClientRequest ? 'Client request' : null;
  const when = x.date === today ? 'Today' : longDay(x.date);
  return (
    <Sheet side={side} width={520} label={x.title} onClose={onClose} crumbs={<><Link to={`/spaces/${x.spaceId}`}>{x.spaceName}</Link><span>/</span><Link to={`/tracks/${x.trackId}`}><b>{x.trackName}</b></Link></>}>
      {state.back && (
        <button type="button" className="dback" onClick={() => onOpen(state.back!)}><IconChevL />{state.back.kind === 'due' ? 'Due today' : longDay(state.back.date)}</button>
      )}
      <div className="dhd"><span className={`ty ${TYPE_CLS[x.type]}`}><i />{TYPE_WORD[x.type]}</span>
        {x.isMilestone && <span className="lbl">◆ Milestone</span>}
        {flag && <span className={`lbl ${x.state.bad ? 'odx' : 'creq'}`}>{flag}</span>}</div>
      <h2 className="stitle" style={{ fontSize: 24 }}>{x.title}</h2>
      <div className="facts">
        <div className="fact"><span className="k">{meeting ? 'When' : 'Due'}</span><div className="v">{when}, {meeting ? `${x.start}${x.end ? ` to ${x.end}` : ''}` : x.start ?? 'no set time'}</div></div>
        <div className="fact"><span className="k">Owner</span><div className="v">{x.owner ?? 'Nobody yet'}</div></div>
        {x.attendees.length > 0 && <div className="fact"><span className="k">Attendees</span><div className="v">{x.attendees.join(', ')}</div></div>}
        {x.requestedBy && <div className="fact"><span className="k">Requested by</span><div className="v">{x.requestedBy}</div></div>}
        {x.waitingOn && <div className="fact"><span className="k">Waiting on</span><div className="v">{x.waitingOn}</div></div>}
      </div>
      {(!meeting || (x.needsPrep && x.stepsTotal > 0)) && (
        <div className="dprog"><Progress value={meeting ? Math.round(100 * x.stepsDone / Math.max(1, x.stepsTotal)) : x.progress} color={progressColor(x)} /><em>{progressText(x)}</em></div>
      )}
      <div className="rowc">
        {meeting ? <>
          <button type="button" className="bt pri" onClick={() => { onClose(); panel.open({ activityId: x.id, spaceId: x.spaceId, label: x.title, prompt: `Brief me for ${x.title}`, send: true }); }}>Brief me</button>
          <button type="button" className="bt" onClick={() => { onClose(); acts.open(x.id); }}>Open {TYPE_WORD[x.type].toLowerCase()}</button>
          <Link className="bt" to={`/record?activity=${x.id}`}>Record it</Link>
        </> : <>
          <button type="button" className="bt pri" onClick={() => { onClose(); acts.open(x.id); }}>Open {TYPE_WORD[x.type].toLowerCase()}</button>
          <button type="button" className="bt" onClick={() => { onClose(); panel.open({ activityId: x.id, spaceId: x.spaceId, label: x.title, prompt: `What is the latest on ${x.title}?`, send: true }); }}>Ask for an update</button>
        </>}
      </div>
    </Sheet>
  );
}
